import React, { useState, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./structuredData.css";

const DAYS = [
  { key: "monday", label: "Lundi", en: "Monday" },
  { key: "tuesday", label: "Mardi", en: "Tuesday" },
  { key: "wednesday", label: "Mercredi", en: "Wednesday" },
  { key: "thursday", label: "Jeudi", en: "Thursday" },
  { key: "friday", label: "Vendredi", en: "Friday" },
  { key: "saturday", label: "Samedi", en: "Saturday" },
  { key: "sunday", label: "Dimanche", en: "Sunday" },
];

const COUNTRIES = [
  { code: "FR", label: "France" },
  { code: "BE", label: "Belgique" },
  { code: "CH", label: "Suisse" },
  { code: "LU", label: "Luxembourg" },
  { code: "CA", label: "Canada" },
  { code: "US", label: "États-Unis" },
  { code: "DE", label: "Allemagne" },
  { code: "ES", label: "Espagne" },
  { code: "IT", label: "Italie" },
  { code: "GB", label: "Royaume-Uni" },
  { code: "PT", label: "Portugal" },
  { code: "NL", label: "Pays-Bas" },
  { code: "MA", label: "Maroc" },
  { code: "TN", label: "Tunisie" },
  { code: "SN", label: "Sénégal" },
  { code: "CI", label: "Côte d'Ivoire" },
];

const BUSINESS_TYPES = [
  "LocalBusiness",
  "Restaurant",
  "BarOrPub",
  "CafeOrCoffeeShop",
  "Bakery",
  "FastFoodRestaurant",
  "IceCreamShop",
  "Store",
  "ClothingStore",
  "ElectronicsStore",
  "Florist",
  "FurnitureStore",
  "GroceryStore",
  "HardwareStore",
  "HomeGoodsStore",
  "JewelryStore",
  "LiquorStore",
  "ShoeStore",
  "SportingGoodsStore",
  "AutoRepair",
  "AutoDealer",
  "DaySpa",
  "HairSalon",
  "BeautySalon",
  "NailSalon",
  "TattooParlor",
  "Dentist",
  "MedicalClinic",
  "Pharmacy",
  "Physician",
  "Veterinarian",
  "Optician",
  "AccountingService",
  "Attorney",
  "InsuranceAgency",
  "RealEstateAgent",
  "TravelAgency",
  "Electrician",
  "Plumber",
  "LocksmithService",
  "MovingCompany",
  "HVACBusiness",
  "Hotel",
  "Hostel",
  "Motel",
  "BedAndBreakfast",
  "FitnessCenter",
  "GolfCourse",
  "ChildCare",
  "Library",
  "ShoppingCenter",
];

const REQUIRED_FIELDS = [
  "name",
  "url",
  "telephone",
  "streetAddress",
  "addressLocality",
  "postalCode",
];

const INIT_FORM = {
  name: "",
  url: "",
  images: "",
  telephone: "",
  streetAddress: "",
  addressLocality: "",
  addressRegion: "",
  postalCode: "",
  addressCountry: "FR",
  facebook: "",
  instagram: "",
  youtube: "",
  x: "",
  tiktok: "",
  id: "",
  priceRange: "",
  latitude: "",
  longitude: "",
  type: "LocalBusiness",
};

const initHours = () =>
  DAYS.reduce(
    (acc, d) => ({
      ...acc,
      [d.key]: { closed: false, allDay: false, slots: [] },
    }),
    {}
  );

const EXAMPLE_FORM = {
  name: "Boulangerie Dupont",
  url: "https://boulangerie-dupont.fr",
  images:
    "https://boulangerie-dupont.fr/images/facade.jpg, https://boulangerie-dupont.fr/images/logo.png",
  telephone: "+33 1 23 45 67 89",
  streetAddress: "15 Rue du Faubourg Saint-Honoré",
  addressLocality: "Paris",
  addressRegion: "Île-de-France",
  postalCode: "75008",
  addressCountry: "FR",
  facebook: "https://facebook.com/boulangerie.dupont",
  instagram: "https://instagram.com/boulangerie_dupont",
  youtube: "",
  x: "",
  tiktok: "",
  id: "https://boulangerie-dupont.fr/#localbusiness",
  priceRange: "€€",
  latitude: "48.8698",
  longitude: "2.3156",
  type: "Bakery",
};

const EXAMPLE_HOURS = DAYS.reduce((acc, day) => {
  if (day.key === "sunday") {
    acc[day.key] = { closed: true, allDay: false, slots: [] };
  } else {
    acc[day.key] = {
      closed: false,
      allDay: false,
      slots: [
        { opens: "07:00", closes: "13:00" },
        { opens: "15:00", closes: "19:30" },
      ],
    };
  }
  return acc;
}, {});

const StructuredData = () => {
  const [form, setForm] = useState(INIT_FORM);
  const [hours, setHours] = useState(initHours);
  const [minified, setMinified] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const formRef = useRef(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateDay = (dayKey, updates) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], ...updates },
    }));
  };

  const addSlot = (dayKey) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: [...prev[dayKey].slots, { opens: "09:00", closes: "18:00" }],
      },
    }));
  };

  const removeSlot = (dayKey, index) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.filter((_, i) => i !== index),
      },
    }));
  };

  const updateSlot = (dayKey, index, field, value) => {
    setHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.map((s, i) =>
          i === index ? { ...s, [field]: value } : s
        ),
      },
    }));
  };

  const copyToAll = (dayKey) => {
    const source = hours[dayKey];
    setHours((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (d.key !== dayKey) {
          next[d.key] = {
            closed: source.closed,
            allDay: source.allDay,
            slots: source.slots.map((s) => ({ ...s })),
          };
        }
      });
      return next;
    });
  };

  const resetDay = (dayKey) => {
    updateDay(dayKey, { closed: false, allDay: false, slots: [] });
  };

  const fillExample = () => {
    setForm(EXAMPLE_FORM);
    setHours(EXAMPLE_HOURS);
    setShowAdvanced(true);
  };

  const resetForm = () => {
    setForm(INIT_FORM);
    setHours(initHours());
    setShowAdvanced(false);
  };

  const jsonLd = useMemo(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": form.type || "LocalBusiness",
    };

    if (form.id) data["@id"] = form.id;
    if (form.name) data.name = form.name;
    if (form.url) data.url = form.url;

    const images = form.images
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (images.length) data.image = images;

    if (form.telephone) data.telephone = form.telephone;
    if (form.priceRange) data.priceRange = form.priceRange;

    const hasAddress =
      form.streetAddress || form.addressLocality || form.postalCode;
    if (hasAddress || form.addressCountry) {
      const address = { "@type": "PostalAddress" };
      if (form.streetAddress) address.streetAddress = form.streetAddress;
      if (form.addressLocality)
        address.addressLocality = form.addressLocality;
      if (form.addressRegion) address.addressRegion = form.addressRegion;
      if (form.postalCode) address.postalCode = form.postalCode;
      if (form.addressCountry) address.addressCountry = form.addressCountry;
      data.address = address;
    }

    if (form.latitude && form.longitude) {
      data.geo = {
        "@type": "GeoCoordinates",
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
      };
    }

    const sameAs = [
      form.facebook,
      form.instagram,
      form.youtube,
      form.x,
      form.tiktok,
    ].filter(Boolean);
    if (sameAs.length) data.sameAs = sameAs;

    const specs = [];
    DAYS.forEach((day) => {
      const h = hours[day.key];
      if (h.closed) return;
      if (h.allDay) {
        specs.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: day.en,
          opens: "00:00",
          closes: "23:59",
        });
      } else {
        h.slots.forEach((slot) => {
          if (slot.opens && slot.closes) {
            specs.push({
              "@type": "OpeningHoursSpecification",
              dayOfWeek: day.en,
              opens: slot.opens,
              closes: slot.closes,
            });
          }
        });
      }
    });
    if (specs.length) data.openingHoursSpecification = specs;

    return data;
  }, [form, hours]);

  const jsonLdScript = useMemo(() => {
    const json = minified
      ? JSON.stringify(jsonLd)
      : JSON.stringify(jsonLd, null, 2);
    return `<script type="application/ld+json">\n${json}\n</script>`;
  }, [jsonLd, minified]);

  const onCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(jsonLd, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "structured-data.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const remainingRequired = REQUIRED_FIELDS.filter(
    (f) => !form[f]?.trim()
  ).length;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="sd-page">
      <Helmet>
        <title>
          Générateur de données structurées locales gratuit | JSON-LD
          LocalBusiness
        </title>
        <meta
          name="description"
          content="Générez gratuitement vos données structurées JSON-LD LocalBusiness pour améliorer votre SEO local. Remplissez le formulaire, copiez le code et collez-le sur votre site."
        />
        <link rel="canonical" href="https://localranker.tech/local-business-structured-data-generator" />
        <meta property="og:title" content="Générateur de données structurées locales gratuit | Localranker" />
        <meta property="og:description" content="Générez gratuitement vos données structurées JSON-LD LocalBusiness pour améliorer votre SEO local." />
        <meta property="og:url" content="https://localranker.tech/local-business-structured-data-generator" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>

      <Hero
        title="Générateur de données structurées locales"
        subtitle="Optimisez votre SEO local avec des données structurées JSON-LD pour votre entreprise. Remplissez le formulaire et copiez le code généré."
        ctaText="Commencer"
        ctaOnClick={scrollToForm}
      />

      <section className="sd-steps">
        <div className="sd-steps__inner">
          <span className="sd-steps__badge">Mode d'emploi</span>
          <h2 className="sd-steps__title">
            Générez vos données structurées en <em>3 étapes</em>
          </h2>
          <div className="sd-steps__grid">
            <div className="sd-steps__card">
              <div className="sd-steps__num">1</div>
              <div className="sd-steps__line" />
              <h3 className="sd-steps__card-title">
                Remplissez vos informations
              </h3>
              <p className="sd-steps__card-desc">
                Nom, URL, adresse, téléphone, horaires et réseaux sociaux
              </p>
            </div>
            <div className="sd-steps__card">
              <div className="sd-steps__num">2</div>
              <div className="sd-steps__line" />
              <h3 className="sd-steps__card-title">
                Copiez le code généré
              </h3>
              <p className="sd-steps__card-desc">
                Collez le JSON-LD dans la balise &lt;head&gt; de votre site web
              </p>
            </div>
            <div className="sd-steps__card">
              <div className="sd-steps__num">3</div>
              <h3 className="sd-steps__card-title">
                Validez avec Google
              </h3>
              <p className="sd-steps__card-desc">
                Testez votre balisage avec le Rich Results Test de Google
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="sd-main" ref={formRef}>
        <div className="sd-main__form">
          {/* Main info */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📝</div>
              <h3 className="sd-card__title">Informations principales</h3>
            </div>
            <div className="sd-card__body">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-name">
                  Nom de l'entreprise *
                </label>
                <input
                  className="sd-input"
                  id="sd-name"
                  name="name"
                  placeholder="Nom"
                  value={form.name}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-url">
                  URL du site *
                </label>
                <input
                  className="sd-input"
                  id="sd-url"
                  name="url"
                  placeholder="https://..."
                  value={form.url}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-images">
                  URL(s) image/logo (séparées par des virgules)
                </label>
                <input
                  className="sd-input"
                  id="sd-images"
                  name="images"
                  placeholder="https://logo.png, https://photo.jpg"
                  value={form.images}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-telephone">
                  Téléphone *
                </label>
                <input
                  className="sd-input"
                  id="sd-telephone"
                  name="telephone"
                  placeholder="+33..."
                  value={form.telephone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📍</div>
              <h3 className="sd-card__title">Adresse</h3>
            </div>
            <div className="sd-card__body">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-street">
                  Rue *
                </label>
                <input
                  className="sd-input"
                  id="sd-street"
                  name="streetAddress"
                  placeholder="12 Rue ..."
                  value={form.streetAddress}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field-row">
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-city">
                    Ville *
                  </label>
                  <input
                    className="sd-input"
                    id="sd-city"
                    name="addressLocality"
                    placeholder="Ville"
                    value={form.addressLocality}
                    onChange={handleChange}
                  />
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-region">
                    Région/Département
                  </label>
                  <input
                    className="sd-input"
                    id="sd-region"
                    name="addressRegion"
                    placeholder="Région"
                    value={form.addressRegion}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="sd-field-row">
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-postal">
                    Code postal *
                  </label>
                  <input
                    className="sd-input"
                    id="sd-postal"
                    name="postalCode"
                    placeholder="75002"
                    value={form.postalCode}
                    onChange={handleChange}
                  />
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-country">
                    Pays *
                  </label>
                  <select
                    className="sd-select"
                    id="sd-country"
                    value={form.addressCountry}
                    onChange={(e) =>
                      handleSelectChange("addressCountry", e.target.value)
                    }
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Social media */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">📱</div>
              <h3 className="sd-card__title">Réseaux sociaux (optionnels)</h3>
            </div>
            <div className="sd-card__body sd-card__body--compact">
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-facebook">
                  Facebook
                </label>
                <input
                  className="sd-input"
                  id="sd-facebook"
                  name="facebook"
                  placeholder="https://facebook.com/..."
                  value={form.facebook}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-instagram">
                  Instagram
                </label>
                <input
                  className="sd-input"
                  id="sd-instagram"
                  name="instagram"
                  placeholder="https://instagram.com/..."
                  value={form.instagram}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-youtube">
                  YouTube
                </label>
                <input
                  className="sd-input"
                  id="sd-youtube"
                  name="youtube"
                  placeholder="https://youtube.com/..."
                  value={form.youtube}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-x">X/Twitter</label>
                <input
                  className="sd-input"
                  id="sd-x"
                  name="x"
                  placeholder="https://twitter.com/..."
                  value={form.x}
                  onChange={handleChange}
                />
              </div>
              <div className="sd-field">
                <label className="sd-label" htmlFor="sd-tiktok">
                  TikTok
                </label>
                <input
                  className="sd-input"
                  id="sd-tiktok"
                  name="tiktok"
                  placeholder="https://tiktok.com/@..."
                  value={form.tiktok}
                  onChange={handleChange}
                />
              </div>
              <p className="sd-hint">
                N'ajoutez que les réseaux sociaux effectivement utilisés.
              </p>
            </div>
          </div>

          {/* Opening hours */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">🕐</div>
              <h3 className="sd-card__title">Horaires d'ouverture</h3>
            </div>
            <div className="sd-card__body">
              {DAYS.map((day) => {
                const h = hours[day.key];
                return (
                  <div key={day.key} className="sd-day">
                    <div className="sd-day__header">
                      <span className="sd-day__name">{day.label}</span>
                      <div className="sd-day__controls">
                        <label className="sd-toggle">
                          <span className="sd-toggle__label">Fermé</span>
                          <button
                            type="button"
                            className={`sd-switch${h.closed ? " sd-switch--on" : ""}`}
                            onClick={() =>
                              updateDay(day.key, { closed: !h.closed })
                            }
                          >
                            <span className="sd-switch__thumb" />
                          </button>
                        </label>
                        <label className="sd-toggle">
                          <span className="sd-toggle__label">24h/24</span>
                          <button
                            type="button"
                            className={`sd-switch${h.allDay ? " sd-switch--on" : ""}`}
                            onClick={() =>
                              updateDay(day.key, { allDay: !h.allDay })
                            }
                          >
                            <span className="sd-switch__thumb" />
                          </button>
                        </label>
                        <button
                          type="button"
                          className="sd-btn sd-btn--sm sd-btn--secondary"
                          onClick={() => copyToAll(day.key)}
                        >
                          Copier vers tous
                        </button>
                        <button
                          type="button"
                          className="sd-btn sd-btn--sm sd-btn--ghost"
                          onClick={() => resetDay(day.key)}
                        >
                          Réinitialiser
                        </button>
                      </div>
                    </div>
                    {!h.closed && !h.allDay && (
                      <div className="sd-day__slots">
                        {h.slots.map((slot, idx) => (
                          <div key={idx} className="sd-slot">
                            <input
                              type="time"
                              className="sd-input sd-input--time"
                              value={slot.opens}
                              onChange={(e) =>
                                updateSlot(day.key, idx, "opens", e.target.value)
                              }
                            />
                            <span className="sd-slot__sep">—</span>
                            <input
                              type="time"
                              className="sd-input sd-input--time"
                              value={slot.closes}
                              onChange={(e) =>
                                updateSlot(day.key, idx, "closes", e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="sd-btn sd-btn--sm sd-btn--ghost sd-btn--danger"
                              onClick={() => removeSlot(day.key, idx)}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="sd-btn sd-btn--primary sd-btn--sm"
                          onClick={() => addSlot(day.key)}
                        >
                          + Ajouter un créneau
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="sd-hint">
                Vérifiez vos horaires après génération.
              </p>
            </div>
          </div>

          {/* Advanced */}
          <div className="sd-card">
            <div className="sd-card__header">
              <div className="sd-card__icon">⚙️</div>
              <h3 className="sd-card__title">Options avancées</h3>
              <button
                type="button"
                className="sd-btn sd-btn--sm sd-btn--outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Masquer" : "Afficher"}
              </button>
            </div>
            {showAdvanced && (
              <div className="sd-card__body">
                <div className="sd-field-row">
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-id">
                      @id (URL canonique)
                    </label>
                    <input
                      className="sd-input"
                      id="sd-id"
                      name="id"
                      placeholder="https://exemple.com/#localbusiness"
                      value={form.id}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-priceRange">
                      priceRange
                    </label>
                    <input
                      className="sd-input"
                      id="sd-priceRange"
                      name="priceRange"
                      placeholder="€€"
                      value={form.priceRange}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="sd-field-row">
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-lat">
                      Latitude
                    </label>
                    <input
                      className="sd-input"
                      id="sd-lat"
                      name="latitude"
                      placeholder="48.8566"
                      value={form.latitude}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="sd-field">
                    <label className="sd-label" htmlFor="sd-lng">
                      Longitude
                    </label>
                    <input
                      className="sd-input"
                      id="sd-lng"
                      name="longitude"
                      placeholder="2.3522"
                      value={form.longitude}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="sd-field">
                  <label className="sd-label" htmlFor="sd-type">
                    Type (@type)
                  </label>
                  <select
                    className="sd-select"
                    id="sd-type"
                    value={form.type}
                    onChange={(e) =>
                      handleSelectChange("type", e.target.value)
                    }
                  >
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="sd-actions">
            <button
              type="button"
              className="sd-btn sd-btn--secondary"
              onClick={fillExample}
            >
              ✨ Remplir un exemple
            </button>
            <button
              type="button"
              className="sd-btn sd-btn--ghost"
              onClick={resetForm}
            >
              🔄 Réinitialiser
            </button>
          </div>
        </div>

        {/* JSON-LD Preview */}
        <div className="sd-main__preview">
          <div className="sd-preview">
            <div className="sd-preview__header">
              <div className="sd-preview__title-row">
                <div className="sd-card__icon">💻</div>
                <h3 className="sd-card__title">JSON-LD Généré</h3>
              </div>
              <label className="sd-toggle">
                <span className="sd-toggle__label">Minifier</span>
                <button
                  type="button"
                  className={`sd-switch${minified ? " sd-switch--on" : ""}`}
                  onClick={() => setMinified(!minified)}
                >
                  <span className="sd-switch__thumb" />
                </button>
              </label>
            </div>
            <pre className="sd-preview__code">{jsonLdScript}</pre>
            <div className="sd-preview__actions">
              <CopyToClipboard text={jsonLdScript} onCopy={onCopy}>
                <button type="button" className="sd-btn sd-btn--accent">
                  {copied ? "✓ Copié !" : "Copier le code"}
                </button>
              </CopyToClipboard>
              <button
                type="button"
                className="sd-btn sd-btn--outline"
                onClick={downloadJson}
              >
                Télécharger .json
              </button>
            </div>
            {remainingRequired > 0 && (
              <p className="sd-preview__remaining">
                Champs requis restants : {remainingRequired}
              </p>
            )}
          </div>
        </div>
      </div>

      <section className="sd-seo">
        <div className="sd-seo__inner">

          <article className="sd-seo__block">
            <h2>Qu'est-ce qu'un générateur de données structurées locales ?</h2>
            <p>
              Un <strong>générateur de données structurées locales</strong> est un outil qui crée automatiquement
              le balisage JSON-LD conforme au vocabulaire <strong>Schema.org</strong> pour votre entreprise locale.
              Ce code, une fois intégré à votre site, permet à Google de mieux comprendre les informations
              essentielles de votre établissement : nom, adresse, horaires, téléphone et bien plus.
            </p>
            <p>
              Les données structurées sont un standard du web sémantique. Elles transforment
              des informations lisibles par l'humain en un format que les moteurs de recherche
              peuvent exploiter directement, améliorant ainsi votre visibilité dans les résultats locaux.
            </p>
          </article>

          <article className="sd-seo__block">
            <h2>Pourquoi les données structurées sont essentielles pour le SEO local ?</h2>
            <p>
              Google utilise les données structurées pour enrichir ses résultats de recherche
              et mieux positionner les entreprises locales. Voici ce que vous gagnez concrètement :
            </p>
            <div className="sd-seo__benefits">
              <div className="sd-seo__benefit">
                <span className="sd-seo__benefit-icon">🎯</span>
                <div>
                  <strong>Meilleur positionnement local</strong>
                  <p>
                    Google privilégie les sites avec des données structurées valides dans le Local Pack
                    et Google Maps, augmentant votre visibilité face à la concurrence.
                  </p>
                </div>
              </div>
              <div className="sd-seo__benefit">
                <span className="sd-seo__benefit-icon">✨</span>
                <div>
                  <strong>Rich snippets enrichis</strong>
                  <p>
                    Affichez vos horaires, votre note, votre adresse directement dans les résultats Google.
                    Les rich snippets augmentent le taux de clic de 20 à 30%.
                  </p>
                </div>
              </div>
              <div className="sd-seo__benefit">
                <span className="sd-seo__benefit-icon">🤖</span>
                <div>
                  <strong>Compréhension par les IA</strong>
                  <p>
                    Les assistants vocaux (Google Assistant, Siri) et les résultats IA s'appuient
                    sur les données structurées pour répondre aux requêtes locales.
                  </p>
                </div>
              </div>
              <div className="sd-seo__benefit">
                <span className="sd-seo__benefit-icon">📊</span>
                <div>
                  <strong>Knowledge Panel</strong>
                  <p>
                    Les données structurées alimentent le Knowledge Panel de votre entreprise,
                    l'encart qui apparaît à droite des résultats Google avec toutes vos informations.
                  </p>
                </div>
              </div>
            </div>
          </article>

          <article className="sd-seo__block">
            <h2>Comment fonctionne le balisage JSON-LD LocalBusiness ?</h2>
            <p>
              Le format <strong>JSON-LD</strong> (JavaScript Object Notation for Linked Data) est le format
              recommandé par Google pour les données structurées. Contrairement aux microdonnées ou RDFa,
              il se place dans une balise <code>&lt;script&gt;</code> dans le <code>&lt;head&gt;</code> de votre page,
              sans modifier votre HTML visible.
            </p>
            <div className="sd-seo__steps-list">
              <div className="sd-seo__step">
                <span className="sd-seo__step-num">1</span>
                <div>
                  <strong>Renseignez vos informations</strong>
                  <p>Nom de l'entreprise, URL, adresse postale complète, téléphone et horaires d'ouverture.</p>
                </div>
              </div>
              <div className="sd-seo__step">
                <span className="sd-seo__step-num">2</span>
                <div>
                  <strong>Le code JSON-LD est généré en temps réel</strong>
                  <p>Notre outil construit automatiquement la structure conforme à Schema.org avec le type LocalBusiness.</p>
                </div>
              </div>
              <div className="sd-seo__step">
                <span className="sd-seo__step-num">3</span>
                <div>
                  <strong>Intégrez le code à votre site</strong>
                  <p>Copiez le code et collez-le dans la balise &lt;head&gt; de votre page. Compatible WordPress, Shopify, Wix et tous les CMS.</p>
                </div>
              </div>
              <div className="sd-seo__step">
                <span className="sd-seo__step-num">4</span>
                <div>
                  <strong>Validez avec l'outil de test Google</strong>
                  <p>Utilisez le Rich Results Test pour vérifier que votre balisage est correctement détecté par Google.</p>
                </div>
              </div>
            </div>
          </article>

          <article className="sd-seo__block">
            <h2>Quels types de données structurées pour le SEO local ?</h2>
            <p>
              Le vocabulaire Schema.org propose de nombreux sous-types de <strong>LocalBusiness</strong>,
              chacun adapté à un secteur d'activité. Utiliser le type le plus spécifique possible
              aide Google à mieux catégoriser votre entreprise :
            </p>
            <div className="sd-seo__types-grid">
              <div className="sd-seo__type-group">
                <h4>Restauration</h4>
                <ul>
                  <li>Restaurant</li>
                  <li>Bakery</li>
                  <li>CafeOrCoffeeShop</li>
                  <li>BarOrPub</li>
                  <li>FastFoodRestaurant</li>
                </ul>
              </div>
              <div className="sd-seo__type-group">
                <h4>Commerce</h4>
                <ul>
                  <li>Store</li>
                  <li>ClothingStore</li>
                  <li>ElectronicsStore</li>
                  <li>GroceryStore</li>
                  <li>JewelryStore</li>
                </ul>
              </div>
              <div className="sd-seo__type-group">
                <h4>Santé &amp; Beauté</h4>
                <ul>
                  <li>Dentist</li>
                  <li>MedicalClinic</li>
                  <li>HairSalon</li>
                  <li>BeautySalon</li>
                  <li>Pharmacy</li>
                </ul>
              </div>
              <div className="sd-seo__type-group">
                <h4>Services</h4>
                <ul>
                  <li>AutoRepair</li>
                  <li>Electrician</li>
                  <li>Plumber</li>
                  <li>RealEstateAgent</li>
                  <li>AccountingService</li>
                </ul>
              </div>
            </div>
            <p>
              Notre générateur propose plus de 50 types. Sélectionnez celui qui correspond
              le mieux à votre activité dans les options avancées du formulaire.
            </p>
          </article>

          <article className="sd-seo__block">
            <h2>Données structurées locales : les champs indispensables</h2>
            <p>
              Pour que Google exploite pleinement vos données structurées, certains champs
              sont particulièrement importants. Voici les priorités :
            </p>
            <div className="sd-seo__fields-grid">
              <div className="sd-seo__field-card sd-seo__field-card--required">
                <span className="sd-seo__field-priority">Requis</span>
                <h4>name</h4>
                <p>Le nom exact de votre établissement, tel qu'il apparaît sur votre fiche Google Business Profile.</p>
              </div>
              <div className="sd-seo__field-card sd-seo__field-card--required">
                <span className="sd-seo__field-priority">Requis</span>
                <h4>address</h4>
                <p>Adresse postale complète incluant rue, ville, code postal et pays.</p>
              </div>
              <div className="sd-seo__field-card sd-seo__field-card--required">
                <span className="sd-seo__field-priority">Requis</span>
                <h4>telephone</h4>
                <p>Numéro de téléphone au format international (+33 pour la France).</p>
              </div>
              <div className="sd-seo__field-card sd-seo__field-card--recommended">
                <span className="sd-seo__field-priority">Recommandé</span>
                <h4>openingHoursSpecification</h4>
                <p>Horaires d'ouverture détaillés pour chaque jour de la semaine.</p>
              </div>
              <div className="sd-seo__field-card sd-seo__field-card--recommended">
                <span className="sd-seo__field-priority">Recommandé</span>
                <h4>geo</h4>
                <p>Coordonnées GPS (latitude/longitude) pour un positionnement précis sur la carte.</p>
              </div>
              <div className="sd-seo__field-card sd-seo__field-card--recommended">
                <span className="sd-seo__field-priority">Recommandé</span>
                <h4>sameAs</h4>
                <p>Liens vers vos profils sur les réseaux sociaux pour renforcer votre entité.</p>
              </div>
            </div>
          </article>

          <article className="sd-seo__block">
            <h2>Questions fréquentes sur les données structurées locales</h2>
            <div className="sd-seo__faq">
              <div className="sd-seo__faq-item">
                <h3>Les données structurées sont-elles obligatoires pour le SEO local ?</h3>
                <p>
                  Non, elles ne sont pas obligatoires, mais elles sont <strong>fortement recommandées</strong> par Google.
                  Les sites avec des données structurées valides ont significativement plus de chances
                  d'apparaître dans les rich snippets et le Local Pack. C'est un avantage concurrentiel
                  que la plupart des entreprises locales négligent encore.
                </p>
              </div>
              <div className="sd-seo__faq-item">
                <h3>Où placer le code JSON-LD sur mon site ?</h3>
                <p>
                  Le code JSON-LD doit être placé dans la balise <code>&lt;head&gt;</code> de votre page d'accueil
                  ou de votre page de contact. Sur WordPress, vous pouvez utiliser un plugin SEO comme Yoast
                  ou Rank Math, ou coller le code directement dans le header via votre thème. Sur Shopify, Wix
                  ou Squarespace, utilisez la zone de code personnalisé dans les paramètres.
                </p>
              </div>
              <div className="sd-seo__faq-item">
                <h3>Puis-je avoir plusieurs blocs JSON-LD sur une même page ?</h3>
                <p>
                  Oui, vous pouvez avoir plusieurs balises <code>&lt;script type="application/ld+json"&gt;</code>
                  sur une même page. C'est même recommandé si vous avez plusieurs types d'entités à décrire
                  (par exemple un LocalBusiness et un FAQ). Google sait les interpréter séparément.
                </p>
              </div>
              <div className="sd-seo__faq-item">
                <h3>Comment vérifier que mes données structurées fonctionnent ?</h3>
                <p>
                  Utilisez le <strong>Rich Results Test</strong> de Google (search.google.com/test/rich-results)
                  pour valider votre balisage. Vous pouvez aussi consulter le rapport "Améliorations"
                  dans la Google Search Console pour suivre la détection de vos données structurées au fil du temps.
                </p>
              </div>
              <div className="sd-seo__faq-item">
                <h3>Ce générateur est-il gratuit ?</h3>
                <p>
                  Oui, notre générateur de données structurées locales est <strong>100% gratuit</strong> et sans limite d'utilisation.
                  Vous pouvez générer autant de balisages JSON-LD que nécessaire pour tous vos établissements.
                  Pour les réseaux multi-établissements, LocalRanker propose une solution de gestion à grande échelle.
                </p>
              </div>
            </div>
          </article>

        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default StructuredData;
